import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion } from "framer-motion";
import Wheel from "./Wheel";
import {
  EVENT_CITY, EVENT_NAME, FALLBACK_PRIZES, GAME_API, GRAND_PRIZE, SITE_URL, standLabel,
} from "./config";
import "./App.css";

const STORAGE_KEY = "optimmo-roue-rent2026-v2";
const METIERS = ["Gestionnaire locatif", "Agent immobilier", "Syndic de copro", "Mandataire", "Directeur d'agence", "Autres"];

// ── Petits utilitaires ──
const store = {
  get(k, area = "localStorage") {
    try { return JSON.parse(window[area].getItem(k)); } catch { return null; }
  },
  set(k, v, area = "localStorage") {
    try { window[area].setItem(k, JSON.stringify(v)); } catch { /* navigation privée */ }
  },
  del(k, area = "localStorage") {
    try { window[area].removeItem(k); } catch { /* ignore */ }
  },
};

function readParams() {
  const q = new URLSearchParams(window.location.search);
  const utm = {
    source: q.get("utm_source") || "",
    medium: q.get("utm_medium") || "",
    campaign: q.get("utm_campaign") || "",
  };
  // On garde les UTM de la première arrivée (QR code) pendant toute la session
  const saved = store.get("optimmo-utm", "sessionStorage");
  if (utm.source || utm.medium || utm.campaign) store.set("optimmo-utm", utm, "sessionStorage");
  const kiosk = ["1", "true", "stand"].includes((q.get("kiosk") || q.get("mode") || "").toLowerCase());
  return { utm: utm.source || utm.medium || utm.campaign ? utm : saved || utm, kiosk };
}

// Sons générés (aucun fichier à charger)
function useSound() {
  const ctxRef = useRef(null);
  const last = useRef(0);
  const unlock = useCallback(() => {
    try {
      if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    } catch { /* pas d'audio */ }
  }, []);
  const beep = useCallback((freq, dur, vol = 0.05, delay = 0, type = "square") => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const t = ctx.currentTime + delay;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(ctx.destination);
    o.start(t);
    o.stop(t + dur + 0.02);
  }, []);
  const tick = useCallback(() => {
    const now = performance.now();
    if (now - last.current < 45) return;
    last.current = now;
    beep(1300, 0.03, 0.03);
  }, [beep]);
  const win = useCallback(() => {
    [523, 659, 784, 1047].forEach((f, i) => beep(f, 0.22, 0.06, i * 0.11, "triangle"));
  }, [beep]);
  return { unlock, tick, win };
}

// ── Application ──
export default function App() {
  const { utm, kiosk } = useMemo(readParams, []);
  const reduceMotion = useReducedMotion();
  const sound = useSound();

  const [prizes, setPrizes] = useState(FALLBACK_PRIZES);
  const [phase, setPhase] = useState("idle"); // idle | form | spinning | result | already
  const [result, setResult] = useState(null);
  const [already, setAlready] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [winnerIndex, setWinnerIndex] = useState(null);

  const rotate = useMotionValue(0);
  const pointerKick = useMotionValue(0);
  const idleAnim = useRef(null);
  const resetTimer = useRef(null);

  // Charge la liste des lots depuis le CRM (source de vérité)
  useEffect(() => {
    fetch(`${GAME_API}/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => { if (cfg?.prizes?.length) setPrizes(cfg.prizes); })
      .catch(() => {});
  }, []);

  // Déjà joué sur cet appareil ? On réaffiche le gain (sauf en mode stand)
  useEffect(() => {
    if (kiosk) return;
    const saved = store.get(STORAGE_KEY);
    if (saved?.prize) {
      setResult({ ...saved, fresh: false });
      setPhase("result");
    }
  }, [kiosk]);

  // Rotation lente "vitrine" tant que personne ne joue
  useEffect(() => {
    if (phase === "idle" || phase === "form") {
      const from = rotate.get();
      idleAnim.current = animate(rotate, from + 360, {
        duration: reduceMotion ? 120 : 45, ease: "linear", repeat: Infinity,
      });
    }
    return () => idleAnim.current?.stop();
  }, [phase, rotate, reduceMotion]);

  const spinTo = useCallback(async (index) => {
    idleAnim.current?.stop();
    const n = prizes.length;
    const seg = 360 / n;
    const current = rotate.get();
    const jitter = (Math.random() - 0.5) * seg * 0.6;
    const wanted = (((-(index * seg + seg / 2) + jitter) % 360) + 360) % 360;
    const base = current + 360 * (reduceMotion ? 2 : 7);
    let target = base - (((base % 360) + 360) % 360) + wanted;
    if (target < base) target += 360;

    let lastSeg = Math.floor(current / seg);
    await animate(rotate, target, {
      duration: reduceMotion ? 2.2 : 6.8,
      ease: [0.12, 0.75, 0.08, 1],
      onUpdate: (v) => {
        const s = Math.floor(v / seg);
        if (s !== lastSeg) {
          lastSeg = s;
          sound.tick();
          animate(pointerKick, [-22, 0], { duration: 0.18, ease: "easeOut" });
        }
      },
    });
  }, [prizes.length, rotate, pointerKick, reduceMotion, sound]);

  const handlePlayed = useCallback(async (data) => {
    setPhase("spinning");
    let idx = prizes.findIndex((p) => p.id === data.prize?.id);
    if (idx < 0) idx = data.prizeIndex ?? 0;
    await spinTo(idx);
    setWinnerIndex(idx);
    sound.win();
    if (navigator.vibrate) navigator.vibrate([60, 40, 120]);
    const res = {
      prize: data.prize,
      code: data.code,
      firstname: data.firstname,
      email: data.email,
      at: new Date().toISOString(),
      index: idx,
    };
    if (!kiosk) store.set(STORAGE_KEY, res);
    setResult({ ...res, fresh: true });
    setPhase("result");
  }, [prizes, spinTo, sound, kiosk]);

  const reset = useCallback(() => {
    clearTimeout(resetTimer.current);
    setResult(null);
    setAlready(null);
    setWinnerIndex(null);
    setPhase("idle");
  }, []);

  // En mode stand : retour automatique à l'accueil après un gain
  useEffect(() => {
    if (kiosk && (phase === "result" || phase === "already")) {
      resetTimer.current = setTimeout(reset, 60000);
    }
    return () => clearTimeout(resetTimer.current);
  }, [kiosk, phase, reset]);

  const grand = prizes.find((p) => p.grand);

  return (
    <div className="page">
      <div className="bg-shapes" aria-hidden>
        <span className="shape s1" /><span className="shape s2" /><span className="shape s3" />
      </div>

      <header className="top">
        <img src="/logo_optimmo.png" alt="Optimmo Énergies" className="logo" />
        <span className="event-badge">
          <span className="dot" /> {EVENT_NAME} · {EVENT_CITY}
        </span>
      </header>

      <main className="main">
        <section className="hero">
          <p className="eyebrow">Jeu concours · 1 participation par personne</p>
          <h1>
            Tournez la roue,<br />
            <span className="hl">100 % gagnant*</span>
          </h1>
          <p className="sub">
            Goodies, remises sur Optimmo… et peut-être <strong>{grand ? GRAND_PRIZE : "le gros lot"}</strong> 🎁
          </p>
        </section>

        <div className={`wheel-zone ${phase === "idle" ? "inviting" : ""}`}>
          <button
            type="button"
            className="wheel-button"
            onClick={() => phase === "idle" && setPhase("form")}
            disabled={phase !== "idle"}
            aria-label="Participer au jeu"
          >
            <Wheel prizes={prizes} rotate={rotate} pointerKick={pointerKick} winnerIndex={winnerIndex} />
          </button>
        </div>

        <div className="actions">
          {phase === "idle" && (
            <motion.button
              type="button"
              className="btn btn-primary btn-xl"
              onClick={() => setPhase("form")}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Je tente ma chance
              <span className="btn-arrow" aria-hidden>→</span>
            </motion.button>
          )}
          {phase === "spinning" && <p className="spinning-label">La roue tourne… 🤞</p>}
        </div>

        <section className="steps" aria-label="Comment ça marche">
          <div className="step"><b>1</b><span>Je laisse mes coordonnées</span></div>
          <div className="step"><b>2</b><span>Je fais tourner la roue</span></div>
          <div className="step"><b>3</b><span>Je récupère mon lot sur {standLabel()}</span></div>
        </section>
      </main>

      <footer className="foot">
        <p>
          *Un lot ou un avantage par participant, dans la limite des stocks disponibles.{" "}
          <button type="button" className="link" onClick={() => setShowRules(true)}>Règlement & données personnelles</button>
        </p>
        <p className="powered">Optimmo Énergies · l'audit énergétique simplifié pour les pros de l'immobilier</p>
      </footer>

      <AnimatePresence>
        {phase === "form" && (
          <FormSheet
            key="form"
            utm={utm}
            onClose={() => setPhase("idle")}
            onRules={() => setShowRules(true)}
            onUnlockAudio={sound.unlock}
            onPlayed={handlePlayed}
            onAlready={(d) => { setAlready(d); setPhase("already"); }}
          />
        )}
        {phase === "result" && result && (
          <ResultCard key="result" result={result} kiosk={kiosk} onReset={reset} />
        )}
        {phase === "already" && (
          <AlreadyCard key="already" info={already} kiosk={kiosk} onReset={reset} />
        )}
        {showRules && <RulesModal key="rules" onClose={() => setShowRules(false)} />}
      </AnimatePresence>
    </div>
  );
}

// ── Formulaire de participation (panneau qui monte du bas sur mobile) ──
function FormSheet({ utm, onClose, onRules, onUnlockAudio, onPlayed, onAlready }) {
  const [form, setForm] = useState({
    firstname: "", lastname: "", email: "", phone: "", company: "", metier: "", consent: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => firstRef.current?.focus({ preventScroll: true }), 350);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstname.trim()) e.firstname = "Prénom requis";
    if (!form.lastname.trim()) e.lastname = "Nom requis";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = "Email invalide";
    if (!form.company.trim()) e.company = "Société requise";
    if (form.phone && !/^[+()\d\s.-]{8,20}$/.test(form.phone.trim())) e.phone = "Numéro invalide";
    if (!form.consent) e.consent = "Merci d'accepter pour participer";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (evt) => {
    evt.preventDefault();
    onUnlockAudio();
    setServerError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await fetch(`${GAME_API}/play`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, activite: form.metier, utm, page_url: window.location.href }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur serveur. Réessayez dans un instant.");
      if (data.alreadyPlayed) return onAlready(data);
      onPlayed({ ...data, email: form.email.trim().toLowerCase() });
    } catch (err) {
      setServerError(
        err instanceof TypeError ? "Connexion impossible. Vérifiez votre réseau et réessayez." : err.message,
      );
      setLoading(false);
    }
  };

  return (
    <motion.div className="overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-title"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 320 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle" aria-hidden />
        <button type="button" className="close" onClick={onClose} aria-label="Fermer">×</button>
        <h2 id="form-title">Encore une étape 🎡</h2>
        <p className="sheet-sub">Vos coordonnées nous servent à vous remettre votre lot.</p>

        <form onSubmit={submit} noValidate>
          <div className="row">
            <Field label="Prénom" error={errors.firstname} required>
              <input ref={firstRef} autoComplete="given-name" value={form.firstname} onChange={set("firstname")} />
            </Field>
            <Field label="Nom" error={errors.lastname} required>
              <input autoComplete="family-name" value={form.lastname} onChange={set("lastname")} />
            </Field>
          </div>
          <Field label="Email professionnel" error={errors.email} required>
            <input type="email" inputMode="email" autoComplete="email" autoCapitalize="off" value={form.email} onChange={set("email")} />
          </Field>
          <Field label="Société" error={errors.company} required>
            <input autoComplete="organization" value={form.company} onChange={set("company")} />
          </Field>
          <Field label="Téléphone" error={errors.phone}>
            <input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} />
          </Field>

          <fieldset className="chips">
            <legend>Votre métier</legend>
            {METIERS.map((a) => (
              <label key={a} className={`chip ${form.metier === a ? "on" : ""}`}>
                <input type="radio" name="metier" value={a} checked={form.metier === a} onChange={set("metier")} />
                {a}
              </label>
            ))}
          </fieldset>

          {/* Pot de miel anti-robots */}
          <input className="hp" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} aria-hidden />

          <label className={`consent ${errors.consent ? "err" : ""}`}>
            <input type="checkbox" checked={form.consent} onChange={set("consent")} />
            <span>
              J'accepte le <button type="button" className="link" onClick={onRules}>règlement</button> et d'être
              recontacté(e) par Optimmo Énergies dans un cadre professionnel.
            </span>
          </label>

          {serverError && <p className="server-error" role="alert">{serverError}</p>}

          <motion.button type="submit" className="btn btn-primary btn-block" disabled={loading} whileTap={{ scale: 0.97 }}>
            {loading ? <span className="spinner" aria-label="Envoi…" /> : "Lancer la roue 🎡"}
          </motion.button>
        </form>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, error, required, children }) {
  return (
    <label className={`field ${error ? "has-error" : ""}`}>
      <span className="field-label">{label}{required && <i aria-hidden> *</i>}</span>
      {React.cloneElement(children, { "aria-invalid": !!error, required })}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

// ── Résultat ──
function ResultCard({ result, kiosk, onReset }) {
  const { prize, code, firstname, email, at, fresh } = result;
  const date = new Date(at);
  const when = date.toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

  return (
    <motion.div className="overlay result-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      {fresh && <Confetti />}
      <motion.div
        className="card result-card"
        role="dialog"
        aria-modal="true"
        initial={{ scale: 0.7, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20, delay: fresh ? 0.15 : 0 }}
      >
        <div className="result-emoji" aria-hidden>{prize?.id === "grandlot" ? "🏆" : "🎉"}</div>
        <p className="result-hello">Bravo{firstname ? ` ${firstname}` : ""} !</p>
        <h2 className="result-prize">{prize?.label}</h2>
        <p className="result-cta">{prize?.cta}</p>

        <div className="ticket">
          <div className="ticket-left">
            <span className="ticket-label">Code de retrait</span>
            <span className="ticket-code">{code}</span>
            <span className="ticket-date">{when}</span>
          </div>
          <div className="ticket-right">
            <span>À présenter sur</span>
            <b>{standLabel()}</b>
          </div>
        </div>
        <p className="result-tip">
          ✉️ Un e-mail de confirmation avec ce code vous est envoyé{email ? <> à <b>{email}</b></> : ""}.
          <br />Montrez-le (ou cet écran) à l'équipe Optimmo.
        </p>

        <div className="result-actions">
          <a className="btn btn-ghost" href={SITE_URL} target="_blank" rel="noopener noreferrer">Découvrir Optimmo</a>
          {kiosk && <button type="button" className="btn btn-primary" onClick={onReset}>Nouvelle partie</button>}
        </div>
      </motion.div>
    </motion.div>
  );
}

function AlreadyCard({ info, kiosk, onReset }) {
  return (
    <motion.div className="overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="card result-card" role="dialog" aria-modal="true" initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <div className="result-emoji" aria-hidden>😉</div>
        <h2 className="result-prize">Vous avez déjà joué !</h2>
        <p className="result-cta">Une seule participation par personne pendant le {EVENT_NAME}.</p>
        {info?.previousGain && (
          <div className="ticket single">
            <div className="ticket-left">
              <span className="ticket-label">Votre gain</span>
              <span className="ticket-prev">{info.previousGain}</span>
            </div>
          </div>
        )}
        <p className="result-cta">
          Votre code de retrait vous a été envoyé par e-mail. Présentez-le sur <b>{standLabel()}</b> pour récupérer votre lot.
        </p>
        <div className="result-actions">
          <a className="btn btn-ghost" href={SITE_URL} target="_blank" rel="noopener noreferrer">Découvrir Optimmo</a>
          {kiosk ? (
            <button type="button" className="btn btn-primary" onClick={onReset}>Nouvelle partie</button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={onReset}>OK</button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function Confetti() {
  const pieces = useMemo(() => {
    const colors = ["#029748", "#f7b800", "#ff7a2f", "#133b3f", "#ffffff"];
    return Array.from({ length: 90 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.4 + Math.random() * 1.8,
      drift: (Math.random() - 0.5) * 160,
      spin: 360 + Math.random() * 720,
      w: 6 + Math.random() * 6,
      h: 10 + Math.random() * 10,
      color: colors[i % colors.length],
      round: i % 4 === 0,
    }));
  }, []);
  return (
    <div className="confetti" aria-hidden>
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            left: `${p.left}vw`,
            width: p.round ? p.w : p.w,
            height: p.round ? p.w : p.h,
            borderRadius: p.round ? "50%" : 2,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--drift": `${p.drift}px`,
            "--spin": `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
}

function RulesModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <motion.div className="overlay top-layer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="card rules" role="dialog" aria-modal="true" aria-labelledby="rules-title"
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose} aria-label="Fermer">×</button>
        <h2 id="rules-title">Règlement du jeu</h2>
        <div className="rules-body">
          <h3>Organisateur</h3>
          <p>Le jeu « Roue Optimmo » est organisé par Optimmo Énergies pendant le {EVENT_NAME} ({EVENT_CITY}).</p>
          <h3>Participation</h3>
          <p>
            Le jeu est gratuit et réservé aux visiteurs professionnels du salon. Une (1) participation par personne
            (même adresse email). Les exposants proposant des services concurrents ne peuvent prétendre à aucun gain.
          </p>
          <h3>Lots</h3>
          <p>
            Chaque participation donne lieu à un tirage au sort aléatoire. Le gros lot ({GRAND_PRIZE}) a une chance sur
            1 000 d'être tiré à chaque participation ; les autres participations gagnent un goodie ou une remise.
            Le lot gagné et son code de retrait sont affichés à l'écran et envoyés par e-mail. Les lots sont à retirer
            sur {standLabel()} pendant la durée du salon, sur présentation du code de retrait. Ils sont nominatifs, non monnayables,
            non échangeables et attribués dans la limite des stocks disponibles. Les remises s'appliquent à une
            nouvelle souscription Optimmo, selon les conditions présentées au stand.
          </p>
          <h3>Fraude</h3>
          <p>
            L'organisateur se réserve le droit d'annuler toute participation en cas de fraude, de tentative de fraude
            ou de non-respect du présent règlement.
          </p>
          <h3>Données personnelles</h3>
          <p>
            Les informations collectées (nom, prénom, email, société, téléphone, métier) sont traitées par
            Optimmo Énergies pour gérer le jeu et vous recontacter dans un cadre professionnel. Elles sont conservées
            au maximum 3 ans après notre dernier contact. Vous pouvez à tout moment demander l'accès, la rectification
            ou la suppression de vos données, ou vous opposer à être recontacté(e), en nous écrivant via{" "}
            <a href={SITE_URL} target="_blank" rel="noopener noreferrer">optimmo-energies.com</a> ou par réponse à nos emails.
          </p>
        </div>
        <button type="button" className="btn btn-primary btn-block" onClick={onClose}>J'ai compris</button>
      </motion.div>
    </motion.div>
  );
}
